import { useEffect, useState } from 'react'

import cn from 'classnames'
import { isEmpty } from 'lodash'

import { RegularLockDetailed } from 'components/icon/regular'
import ImageComponent from 'components/image'
import Link from 'components/link'
import { getCategoryURL } from 'components/molecules/autocomplete/utils'
import FavouriteButton from 'components/molecules/favourite-button'
import ProductTag from 'components/product-tag'
import SkeletonLoading from 'components/skeleton-loading'
import { usePutFavouriteProduct } from 'features/retailer/favourites/hooks/use-put-favourite-product'

import { useTranslate } from 'hooks'
import { useNavPromo } from 'hooks/use-nav-promo'

import { ErrorResponse } from 'services/error-response'
import { hasDiscount } from 'utils/product'
import { fireProductClickTrackingEvent } from 'utils/search/insights'

import { PRODUCT_CARD_IMAGE_ASPECT_RATIO } from 'constants/product'
import { ROUTES } from 'constants/routes'

import BlurImage from './blur-image'
import css from './product-card.module.scss'

export type ProductCategorySimple = {
  id: string
  name: string
  nameEn: string
  key: string
  externalId?: string
}

export type Product = {
  id?: string
  isBestSeller?: boolean
  keyParam: string
  name: string
  image: string
  categories: ProductCategorySimple[]
  brand?: {
    id: string
    name: string | null
    location: string | null
  }
  isNew?: boolean
  minimumOrderQuantity: string
  favorite?: boolean
  wholeSalePrice: string | null
  discountedPrice: string | null
  msrp: string
  promotionName?: string
  size?: 'md' | 'auto'
  isLoading?: boolean
  originalPrice?: boolean
  onSalePercentage?: number | null
  isPublished?: boolean
}

type ProductCardProps = Product & {
  className?: string
  openSignUp?: () => void
  isBrandPreview?: boolean
  isLoggedIn?: boolean
  isFavourite?: boolean
  onFavouriteSuccess?: () => unknown
  onFavouriteError?: (errors: ErrorResponse) => unknown
}

const ProductCard = ({
  id: productId,
  isBestSeller,
  keyParam,
  name,
  categories,
  brand,
  wholeSalePrice,
  discountedPrice,
  onSalePercentage,
  isNew,
  msrp,
  image,
  className,
  openSignUp,
  isBrandPreview,
  size = 'auto',
  isLoading = false,
  isFavourite: _isFavourite = false,
  onFavouriteSuccess,
  onFavouriteError,
  isLoggedIn = false,
}: ProductCardProps) => {
  const { t } = useTranslate('common')
  const productDetailPagePath = ROUTES.PRODUCT.replace('[key]', keyParam)
  const brandPagePath = ROUTES.BRAND.replace('[brandKey]', brand?.id ?? '')

  const [isFavourite, setIsFavourite] = useState<boolean>(_isFavourite)
  const { mutateAsync: setFavouriteProduct } = usePutFavouriteProduct(
    () => {
      setIsFavourite(!isFavourite)
      onFavouriteSuccess?.()
    },
    (error) => onFavouriteError?.(error)
  )

  useEffect(() => {
    setIsFavourite(_isFavourite)
  }, [_isFavourite])

  const isDiscount = hasDiscount(wholeSalePrice, discountedPrice)

  const promotion = useNavPromo(brand?.id ?? '')

  return (
    <div className={cn(css.container, css[`container--${size}`], className)}>
      <div className={css.cardTopWrapper}>
        <div className={css.imageContainer}>
          <div className={css.heart}>
            <FavouriteButton
              isSolid={isFavourite}
              onToggle={
                isLoggedIn
                  ? () => {
                      void setFavouriteProduct(keyParam)
                      !isFavourite &&
                        productId &&
                        fireProductClickTrackingEvent('product_favourite_add', productId, name)
                    }
                  : openSignUp
              }
            />
          </div>
          <ProductTag
            promotion={promotion}
            onSalePercentage={onSalePercentage}
            isBestSeller={isBestSeller}
            isNew={isNew}
            className={css.tag}
          />
          <Link href={isBrandPreview ? '#/' : productDetailPagePath} target="_blank" className={css.imageLink}>
            <ImageComponent
              src={image}
              className={css.image}
              blurDataURL={BlurImage}
              placeholder="blur"
              alt={name + t('a11y.image')}
              objectFit="cover"
              width={292 * PRODUCT_CARD_IMAGE_ASPECT_RATIO}
              height={292}
              priority={true}
              onClick={() => {
                productId && fireProductClickTrackingEvent('product_card_click', productId, name)
              }}
            />
          </Link>
        </div>
      </div>

      <div className={css.cardBottomWrapper}>
        <div className={css.productNames}>
          {!!brand?.name && (
            <Link className={css.brand} href={isBrandPreview ? '#/' : brandPagePath} target="_blank">
              {brand.name}
            </Link>
          )}
          <Link
            className={css.name}
            href={isBrandPreview ? '#/' : productDetailPagePath}
            target="_blank"
            onClick={() => !!productId && fireProductClickTrackingEvent('product_card_click', productId, name)}
          >
            {name}
          </Link>
          {isLoading ? (
            <div className={css.productPricesSkeletons}>
              <SkeletonLoading width="60%" />
              <SkeletonLoading width="40%" />
            </div>
          ) : (
            <>
              <p className={css.wholeSalePriceWrapper} data-test-id="unlocked-wholesale-price">
                {!!wholeSalePrice && <span className={css[isDiscount ? 'oldPrice' : 'price']}>{wholeSalePrice}</span>}
                {!!wholeSalePrice && !!isDiscount && <span className={css.discountedPrice}>{discountedPrice}</span>}
              </p>
              {!wholeSalePrice && (
                <div className={css.unlockWholesalePriceWrapper} onClick={openSignUp}>
                  <p className={css.unlockWholesalePrice}>{t('product_card.unlock_wholesale_price')}</p>
                  <RegularLockDetailed />
                </div>
              )}
              <p className={css.msrp}>
                {msrp} {t('product_card.suggest_retail')}
              </p>
            </>
          )}
        </div>
        <div className={css.productPrices}>
          {!isEmpty(categories) && (
            <Link
              href={isBrandPreview ? '#/' : `${getCategoryURL(categories.map((c) => c.nameEn))}`}
              className={css.subCategory}
              target="_blank"
            >
              {categories[categories.length - 1].name}
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductCard
